/**
 * StepWelcome - Écran d'accueil Go Sport
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Dumbbell, Zap, Target, Clock, BarChart3, PlayCircle, X } from 'lucide-react';

export function StepWelcome({ onStart, onQuickStart, onShowHistory, onResumeSession, onDiscardSession, hasProfile, stats, interruptedSession }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center text-center px-4 py-6 space-y-6"
    >
      {/* Hero */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', delay: 0.1 }}
        className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 to-red-500
          flex items-center justify-center shadow-xl shadow-orange-500/30"
      >
        <Dumbbell className="w-12 h-12 text-white" />
      </motion.div>

      <div>
        <h2 className="text-2xl font-bold text-white">Go Sport</h2>
        <p className="text-zinc-400 mt-1">Ton coach sportif TDAH-friendly</p>
      </div>

      {/* Stats rapides si profil existant */}
      {hasProfile && stats && (
        <div className="grid grid-cols-3 gap-3 w-full">
          <div className="bg-zinc-800/60 rounded-xl p-3 border border-zinc-700/50">
            <Target className="w-5 h-5 text-orange-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{stats.totalSessions}</p>
            <p className="text-[10px] text-zinc-500">Séances</p>
          </div>
          <div className="bg-zinc-800/60 rounded-xl p-3 border border-zinc-700/50">
            <Zap className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{stats.streak?.current || 0}</p>
            <p className="text-[10px] text-zinc-500">Streak</p>
          </div>
          <div className="bg-zinc-800/60 rounded-xl p-3 border border-zinc-700/50">
            <Clock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">
              {Math.round((stats.totalDuration || 0) / 60)}
            </p>
            <p className="text-[10px] text-zinc-500">Min total</p>
          </div>
        </div>
      )}

      {/* Interrupted session resume */}
      {interruptedSession && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 space-y-2"
        >
          <p className="text-sm font-medium text-amber-300">Séance interrompue</p>
          <p className="text-xs text-amber-400/70">
            Tu peux reprendre ta séance là où tu l'as laissée
          </p>
          <div className="flex gap-2">
            <button
              onClick={onResumeSession}
              className="flex-1 py-2 rounded-lg bg-amber-500/20 text-amber-300 text-sm font-medium
                flex items-center justify-center gap-1 hover:bg-amber-500/30 transition-colors"
            >
              <PlayCircle className="w-4 h-4" /> Reprendre
            </button>
            <button
              onClick={onDiscardSession}
              className="py-2 px-3 rounded-lg bg-zinc-800 text-zinc-500 text-sm
                flex items-center justify-center hover:text-zinc-300 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Features */}
      <div className="space-y-3 w-full text-left">
        {[
          { icon: '🎯', text: 'Évaluation de ton niveau sportif' },
          { icon: '📊', text: 'Séances HIIT adaptées à ton profil' },
          { icon: '⏱️', text: 'Minuteur intelligent avec coaching vocal' },
          { icon: '🍌', text: 'Conseils nutrition et récupération' },
        ].map((feat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + i * 0.1 }}
            className="flex items-center gap-3 bg-zinc-800/40 rounded-xl p-3 border border-zinc-700/30"
          >
            <span className="text-xl">{feat.icon}</span>
            <span className="text-sm text-zinc-300">{feat.text}</span>
          </motion.div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 w-full pt-2">
        {hasProfile && (
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={onQuickStart}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500
              text-white font-bold text-lg shadow-lg shadow-orange-500/30
              flex items-center justify-center gap-2"
          >
            <Zap className="w-5 h-5" />
            Lancer une séance
          </motion.button>
        )}

        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={onStart}
          className={`w-full py-4 rounded-2xl font-bold text-lg
            flex items-center justify-center gap-2
            ${hasProfile
              ? 'bg-zinc-800 text-zinc-300 border border-zinc-700'
              : 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/30'
            }`}
        >
          <Dumbbell className="w-5 h-5" />
          {hasProfile ? "Refaire l'évaluation" : "Commencer l'évaluation"}
        </motion.button>

        {hasProfile && (
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={onShowHistory}
            className="w-full py-3 rounded-2xl bg-zinc-800/60 text-zinc-400 border border-zinc-700/50
              font-medium flex items-center justify-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            Ma progression
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}
